import React, { createContext, useContext, useState, useEffect } from 'react';
import authService from '../services/authService';

/**
 * Auth Context
 * 🔐 Provides authentication state and methods to the app
 */
const AuthContext = createContext();

// Custom hook to use the auth context
export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
};

// Auth provider component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Check authentication state on mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const token = localStorage.getItem('token');
        const storedUser = localStorage.getItem('user');
        
        if (token && storedUser) {
          const parsedUser = JSON.parse(storedUser);
          setUser(parsedUser);
          setIsAuthenticated(true);
          
          // Verify token is still valid
          try {
            const response = await authService.getCurrentUser();
            // Update user with latest data including partner info
            setUser(response.user);
            localStorage.setItem('user', JSON.stringify(response.user));
            console.log('✅ Token validated successfully');
          } catch (err) {
            // If token is invalid, clear auth state
            console.log('❌ Token validation failed:', err);
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            setUser(null);
            setIsAuthenticated(false);
          }
        } else {
          setUser(null);
          setIsAuthenticated(false);
        }
      } catch (err) {
        console.error('❌ Auth check failed:', err);
        setError(err.message);
        setUser(null);
        setIsAuthenticated(false);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  // Register a new user
  const register = async (userData) => {
    try {
      setError(null);
      setLoading(true);
      const data = await authService.register(userData);
      console.log('Registration response data:', data);
      
      // Store user data in context and localStorage regardless of verification status
      setUser(data.user);
      setIsAuthenticated(true);
      
      setLoading(false);
      return data;
    } catch (err) {
      setError(err.response?.data?.message || 'Registration failed');
      setLoading(false);
      throw err;
    }
  };

  // Login user
  const login = async (credentials) => {
    try {
      setError(null);
      setLoading(true);
      const data = await authService.login(credentials);
      setUser(data.user);
      setIsAuthenticated(true);
      setLoading(false);
      return data;
    } catch (err) {
      setError(err.response?.data?.message || 'Login failed');
      setLoading(false);
      throw err;
    }
  };

  // Logout user
  const logout = async () => {
    try {
      setError(null);
      setLoading(true);
      await authService.logout();
      setUser(null);
      setIsAuthenticated(false);
      setLoading(false);
    } catch (err) {
      setError(err.message || 'Logout failed');
      setLoading(false);
      // Still clear user state even if API call fails
      setUser(null);
      setIsAuthenticated(false);
    }
  };

  // Generate partner invitation
  const generatePartnerInvite = async (partnerEmail) => {
    try {
      setError(null);
      setLoading(true);
      const data = await authService.generatePartnerInvite(partnerEmail);
      setLoading(false);
      return data;
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to generate partner invitation');
      setLoading(false);
      throw err;
    }
  };

  // Accept partner invitation
  const acceptPartnerInvite = async (invitationIdOrToken, isToken = false) => {
    try {
      setError(null);
      setLoading(true);
      const data = await authService.acceptPartnerInvite(invitationIdOrToken, isToken);
      
      // Force refresh user data to get updated partner information
      await refreshUserData();
      
      setLoading(false);
      return data;
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to accept partner invitation');
      setLoading(false);
      throw err;
    }
  };

  // Reject partner invitation
  const rejectPartnerInvite = async (invitationId) => {
    try {
      setError(null);
      setLoading(true);
      const data = await authService.rejectPartnerInvite(invitationId);
      setLoading(false);
      return data;
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to reject partner invitation');
      setLoading(false);
      throw err;
    }
  };

  // Get partner invitations
  const getPartnerInvitations = async () => {
    try {
      setError(null);
      const data = await authService.getPartnerInvitations();
      return data;
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to get partner invitations');
      throw err;
    }
  };

  // Search users
  const searchUsers = async (searchTerm) => {
    try {
      setError(null);
      const data = await authService.searchUsers(searchTerm);
      return data.users;
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to search users');
      throw err;
    }
  };

  // Send partner invitation
  const sendPartnerInvitation = async (recipientId) => {
    try {
      setError(null);
      setLoading(true);
      const data = await authService.sendPartnerInvitation(recipientId);
      setLoading(false);
      return data;
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send partner invitation');
      setLoading(false);
      throw err;
    }
  };

  // Update privacy settings
  const updatePrivacySettings = async (privacySettings) => {
    try {
      setError(null);
      setLoading(true);
      const data = await authService.updatePrivacySettings(privacySettings);
      
      // Update user with new privacy settings
      const updatedUser = { ...user, privacySettings: data.privacySettings };
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
      
      setLoading(false);
      return data;
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update privacy settings');
      setLoading(false);
      throw err;
    }
  };

  // Update user profile data
  const updateUser = async (userData) => {
    try {
      setError(null);
      setLoading(true);
      const data = await authService.updateUser(userData);
      
      // Update user with new data
      const updatedUser = { ...user, ...data.user };
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
      
      setLoading(false);
      return data;
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update user profile');
      setLoading(false);
      throw err;
    }
  };

  // Change user password
  const changePassword = async (currentPassword, newPassword) => {
    try {
      setError(null);
      setLoading(true);
      const data = await authService.changePassword(currentPassword, newPassword);
      setLoading(false);
      return data;
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to change password');
      setLoading(false);
      throw err;
    }
  };

  // Refresh user data
  const refreshUserData = async () => {
    try {
      setError(null);
      const data = await authService.getCurrentUser();
      setUser(data.user);
      localStorage.setItem('user', JSON.stringify(data.user));
      return data.user;
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to refresh user data');
      throw err;
    }
  };

  // Create partner profile
  const createPartnerProfile = async (partnerData) => {
    try {
      setError(null);
      setLoading(true);
      const data = await authService.createPartnerProfile(partnerData);
      
      // Update user with partner info
      const updatedUser = { 
        ...user, 
        hasPartner: true, 
        partner: data.partner,
        isCreatedPartner: true 
      };
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
      
      setLoading(false);
      return data;
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create partner profile');
      setLoading(false);
      throw err;
    }
  };

  // Update partner profile
  const updatePartnerProfile = async (partnerData) => {
    try {
      setError(null);
      setLoading(true);
      const data = await authService.updatePartnerProfile(partnerData);
      
      // Update user with updated partner info
      const updatedUser = { 
        ...user, 
        partner: data.partner 
      };
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
      
      setLoading(false);
      return data;
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update partner profile');
      setLoading(false);
      throw err;
    }
  };

  // Remove partner
  const removePartner = async () => {
    try {
      setError(null);
      setLoading(true);
      const data = await authService.removePartner();
      
      // Update user to remove partner
      const updatedUser = { 
        ...user, 
        hasPartner: false, 
        partner: null,
        isCreatedPartner: false 
      };
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
      
      setLoading(false);
      return data;
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to remove partner');
      setLoading(false);
      throw err;
    }
  };

  // Verify email with code
  const verifyEmail = async (verificationData) => {
    try {
      setError(null);
      setLoading(true);
      const data = await authService.verifyEmail(verificationData);
      
      // Update user verification status
      const updatedUser = { ...user, isEmailVerified: true };
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
      
      setLoading(false);
      return data;
    } catch (err) {
      setError(err.response?.data?.message || 'Email verification failed');
      setLoading(false);
      throw err;
    }
  };

  // Resend verification code
  const resendVerificationCode = async (email) => {
    try {
      setError(null);
      setLoading(true);
      const data = await authService.resendVerificationCode({ email });
      setLoading(false);
      return data;
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to resend verification code');
      setLoading(false);
      throw err;
    }
  };

  // Cancel partner invitation
  const cancelPartnerInvite = async (invitationId) => {
    try {
      setError(null);
      setLoading(true);
      const data = await authService.cancelPartnerInvite(invitationId);
      setLoading(false);
      return data;
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to cancel partner invitation');
      setLoading(false);
      throw err;
    }
  };

  // Resend partner invitation
  const resendPartnerInvite = async (invitationId) => {
    try {
      setError(null);
      setLoading(true);
      const data = await authService.resendPartnerInvite(invitationId);
      setLoading(false);
      return data;
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to resend partner invitation');
      setLoading(false);
      throw err;
    }
  };

  // Value object to be provided by the context
  const value = {
    user,
    loading,
    error,
    isAuthenticated,
    register,
    login,
    logout,
    generatePartnerInvite,
    acceptPartnerInvite,
    rejectPartnerInvite,
    cancelPartnerInvite,
    resendPartnerInvite,
    getPartnerInvitations,
    searchUsers,
    sendPartnerInvitation,
    updatePrivacySettings,
    updateUser,
    changePassword,
    refreshUserData,
    createPartnerProfile,
    updatePartnerProfile,
    removePartner,
    verifyEmail,
    resendVerificationCode
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext; 